import optimizelyClient from '../utils/optimizely-fs';
import constants from '../components/App/constants';
import datadogRumClient from '../utils/datadog-rum';

const { FEATURES } = constants;

const addVariationToCookieAnalyticsIdentifyAndCallback = (
  featureName,
  variationKey,
  variables,
  enabled,
  cookieName,
  segmentPropertyName,
  onVariationCallback
) => {
  // If there's a variation, then add the cookie
  if (variationKey && cookieName) {
    document.cookie = `${cookieName} = ${variationKey}`;
  }

  // 03/01/2021: No longer use segment properties - only fire events
  // If we want a segment property, set it here
  if (variationKey && segmentPropertyName) {
    if (window.analytics && typeof window.analytics.identify === `function`) {
      window.analytics.identify({
        [segmentPropertyName]: variationKey
      });
    }
  }

  // If feature name starts with team name prefix, let's clean it up
  const shortenedFeatureName = featureName
    ? featureName.substring(featureName.indexOf('_') + 1)
    : null;

  // If there is additional work to do, use a callback
  if (onVariationCallback) {
    onVariationCallback(variationKey, variables, enabled, shortenedFeatureName);
  }
};

export const setABToggleState = async ({
  experimentName,
  anonymousId,
  attributes,
  cookieName = null,
  segmentPropertyName = null,
  onVariationCallback = null
}) => {
  const result = await optimizelyClient.activate(
    experimentName,
    { anonymousId },
    attributes
  );
  const { variationKey, variables, enabled } = result;

  addVariationToCookieAnalyticsIdentifyAndCallback(
    variationKey,
    variables,
    enabled,
    cookieName,
    segmentPropertyName,
    onVariationCallback
  );
};

export const setToggleWithNonactivatingLogic = async ({
  experimentName,
  anonymousId,
  attributes,
  cookieName = null,
  segmentPropertyName = null,
  onVariationCallback = null
}) => {
  const result = await optimizelyClient.getVariation(
    experimentName,
    { anonymousId },
    attributes
  );
  const { variationKey, variables } = result;
  const enabled = variationKey !== '';

  addVariationToCookieAnalyticsIdentifyAndCallback(
    variationKey,
    variables,
    enabled,
    cookieName,
    segmentPropertyName,
    onVariationCallback
  );
};

export const setStandardToggle = async ({
  featureName,
  anonymousId,
  attributes,
  cookieName = null,
  segmentPropertyName = null,
  onVariationCallback = null
}) => {
  const result = await optimizelyClient.isFeatureEnabled(
    featureName,
    { anonymousId },
    attributes
  );
  const { variationKey, variables, enabled } = result;

  addVariationToCookieAnalyticsIdentifyAndCallback(
    featureName,
    variationKey,
    variables,
    enabled,
    cookieName,
    segmentPropertyName,
    onVariationCallback
  );
};

function getExperimentToggles() {
  // This is a function because we could need to have checks around what feature toggles we fetch, like with pma and partners
  return [FEATURES.planFlow, FEATURES.modularReduxEnabled];
}

export const getFeatureToggles = async (
  anonymousId,
  { userAgent, utmSource, planFlow }
) => {
  try {
    optimizelyClient.start();
    const attributes = {
      userAgent,
      utmSource,
      planFlow
    };

    const featureTogglesToFetch = getExperimentToggles();
    const featureToggles = {};
    const promises = [];

    featureTogglesToFetch.forEach(featureName => {
      promises.push(
        setStandardToggle({
          anonymousId,
          attributes,
          featureName,
          cookieName: `${featureName}Variation`,
          segmentPropertyName: `${featureName}Variation`,
          onVariationCallback: (variationKey, variables, enabled, featName) => {
            featureToggles[featName] = {
              ...variables,
              enabled,
              variationKey
            };
          }
        })
      );
    });
    await Promise.all(promises);

    return featureToggles;
  } catch (err) {
    datadogRumClient.addAction('Could not fetch server side feature toggles', {
      err
    });
  }
};
